/**
 * MODULE: AssistantConnectorsMenu.tsx
 * PURPOSE: Perplexity-style + menu with Connectors submenu (flows + MCP tools).
 */

import {
  ArrowRightIcon,
  BookOpenIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PlusIcon,
  PuzzlePieceIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AssistantConnectorItem } from './assistantConnectorTypes';

type AssistantConnectorsMenuProps = {
  catalog: AssistantConnectorItem[];
  loading?: boolean;
  error?: string | null;
  isSelected: (item: AssistantConnectorItem) => boolean;
  onToggle: (item: AssistantConnectorItem) => void;
  activeCount: number;
  disabled?: boolean;
  tone?: 'default' | 'immersive';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  panel?: 'root' | 'connectors';
  onPanelChange?: (panel: 'root' | 'connectors') => void;
};

function ConnectorIcon({ item }: { item: AssistantConnectorItem }) {
  const className = 'h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400';
  if (item.kind === 'web') return <GlobeAltIcon className={className} aria-hidden />;
  if (item.kind === 'mcp_tool') return <PuzzlePieceIcon className={className} aria-hidden />;
  return <Squares2X2Icon className={className} aria-hidden />;
}

export function AssistantConnectorsMenu({
  catalog,
  loading = false,
  error = null,
  isSelected,
  onToggle,
  activeCount,
  disabled = false,
  tone = 'default',
  open: openProp,
  onOpenChange,
  panel: panelProp,
  onPanelChange,
}: AssistantConnectorsMenuProps) {
  const [openInternal, setOpenInternal] = useState(false);
  const [panelInternal, setPanelInternal] = useState<'root' | 'connectors'>('root');
  const open = openProp ?? openInternal;
  const setOpen = onOpenChange ?? setOpenInternal;
  const panel = panelProp ?? panelInternal;
  const setPanel = onPanelChange ?? setPanelInternal;
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        (item.description ?? '').toLowerCase().includes(q)
    );
  }, [catalog, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setPanel('root');
        setQuery('');
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setPanel('root');
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const triggerClass =
    'rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid="assistant-attach-menu-button"
        disabled={disabled}
        onClick={() => {
          setOpen(!open);
          setPanel('root');
          setQuery('');
        }}
        className={triggerClass}
        aria-label="Add sources and attachments"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <PlusIcon className="h-5 w-5" />
      </button>

      {open ? (
        <div
          className="absolute bottom-full left-0 z-[80] mb-2 flex min-w-[15rem] items-start gap-0"
          role="presentation"
        >
          <div
            role="menu"
            aria-label="Composer attachments"
            className="w-56 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-600 dark:bg-gray-800"
          >
            <button
              type="button"
              role="menuitem"
              disabled
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-400"
            >
              <PaperClipIcon className="h-4 w-4 shrink-0" aria-hidden />
              Upload files or images
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid="assistant-connectors-menu-entry"
              onClick={() => setPanel('connectors')}
              className={[
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition',
                panel === 'connectors'
                  ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                  : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/60',
              ].join(' ')}
            >
              <PuzzlePieceIcon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1">Connectors</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{activeCount}</span>
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
            </button>
            <button
              type="button"
              role="menuitem"
              disabled
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-400"
            >
              <BookOpenIcon className="h-4 w-4 shrink-0" aria-hidden />
              Spaces
              <ChevronRightIcon className="ml-auto h-4 w-4 shrink-0" aria-hidden />
            </button>
          </div>

          {panel === 'connectors' ? (
            <div
              role="menu"
              aria-label="Connectors"
              data-testid="assistant-connectors-panel"
              className="ml-1 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-600 dark:bg-gray-800"
            >
              <div className="border-b border-gray-100 px-3 py-2 dark:border-gray-700">
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-gray-600 dark:bg-gray-900/40">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" aria-hidden />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search connectors"
                    data-testid="assistant-connectors-search"
                    className="min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 dark:text-white"
                  />
                </div>
              </div>

              <ul className="max-h-64 overflow-y-auto py-1">
                {loading ? (
                  <li className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">Loading…</li>
                ) : error ? (
                  <li className="px-3 py-3 text-sm text-amber-700 dark:text-amber-300">{error}</li>
                ) : filtered.length === 0 ? (
                  <li className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                    No connectors match your search.
                  </li>
                ) : (
                  filtered.map((item) => {
                    const checked = isSelected(item);
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={checked}
                          data-testid="assistant-connector-option"
                          data-connector-id={item.id}
                          onClick={() => onToggle(item)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/60"
                        >
                          <ConnectorIcon item={item} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                              {item.label}
                            </span>
                            {item.description ? (
                              <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                {item.description}
                              </span>
                            ) : null}
                          </span>
                          <span
                            className={[
                              'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                              checked
                                ? 'border-primary-600 bg-primary-600 text-white'
                                : 'border-gray-300 bg-white dark:border-gray-500 dark:bg-gray-800',
                            ].join(' ')}
                            aria-hidden
                          >
                            {checked ? '✓' : ''}
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>

              <div className="border-t border-gray-100 dark:border-gray-700">
                <Link
                  to="/settings"
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60"
                >
                  <Cog6ToothIcon className="h-4 w-4 shrink-0" aria-hidden />
                  Manage connectors
                  <ArrowRightIcon className="ml-auto h-3.5 w-3.5 text-gray-400" aria-hidden />
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
